import React, { useEffect, useState } from "react";
import { DetailsBaseDialog } from "@/util-components/dialog/base-dialogs/details-base-dialog";
import { Pencil } from "lucide-react";
import type { AdvertisementListResponseDto, EditAdvertisementRequestDto } from "@/lib/api/interfaces/advertisements.interface";
import { useAdvertisementsUpdate } from "@/lib/api/requests/advertisements.requests";
import { Switch } from "@/ui-components/ui/switch";
import { Label } from "@/ui-components/ui/label";

interface EditAdvertisementDialogProps {
  open: boolean;
  advertisement: AdvertisementListResponseDto | null;
  onClose: (updated?: boolean) => void;
}

const EditAdvertisementDialog: React.FC<EditAdvertisementDialogProps> = ({ open, advertisement, onClose }) => {
  const [name, setName] = useState("");
  const [maxConcurrent, setMaxConcurrent] = useState<string>("1");
  const [volume, setVolume] = useState<string>("0");
  const [interval, setInterval] = useState<string>("");
  const [duration, setDuration] = useState<string>("");
  const [showOnSocial, setShowOnSocial] = useState(false);
  const [isDonation, setIsDonation] = useState(false);

  const updateMutation = useAdvertisementsUpdate();

  useEffect(() => {
    if (advertisement && open) {
      setName(advertisement.name || "");
      setMaxConcurrent(String(advertisement.max_concurrent_allowed ?? 1));
      setVolume(String(advertisement.volume ?? 0));
      setInterval(advertisement.interval || "");
      setDuration(advertisement.duration != null ? String(advertisement.duration) : "");
      setShowOnSocial(!!advertisement.show_on_social_media);
      setIsDonation(advertisement.type === "DONATION");
    }
  }, [advertisement, open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!advertisement) return;

    const payload: EditAdvertisementRequestDto = {};
    if (name.trim() !== "") payload.name = name.trim();
    if (maxConcurrent.trim() !== "") payload.max_concurrent_allowed = Number(maxConcurrent);
    if (volume.trim() !== "") payload.volume = Number(volume);
    if (interval.trim() !== "") payload.interval = interval as any;
    if (duration.trim() !== "") payload.duration = Number(duration);
    payload.show_on_social_media = showOnSocial;
    payload.type = isDonation ? "DONATION" : "SPONSORING";

    try {
      await updateMutation.mutateAsync({ id: advertisement._id, data: payload });
      onClose(true);
    } catch {
      // Fehleranzeige kann später ergänzt werden
    }
  };

  const isBusy = updateMutation.isPending;

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onClose}
      mode="edit"
      icon={Pencil}
      title="Leistung bearbeiten"
      description="Passe die Eigenschaften dieser Leistung an. Änderungen wirken sich auf künftige Agreements aus."
      hideDelete={true}
      hideEdit={true}
      isSaving={isBusy}
      canSave={true}
      formId="edit-advertisement-form"
      detailsContent={<></>} // Fallback if somehow switched
      editContent={() => (
        <form id="edit-advertisement-form" onSubmit={(e) => {
          handleSubmit(e);
        }} className="flex flex-col h-full px-5 sm:px-6 py-4 overflow-y-auto space-y-4">
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">Name der Leistung</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-700">Max. gleichzeitig erlaubt</label>
              <input
                type="number"
                min={1}
                value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Betrag in €</label>
              <input
                type="number"
                min={0}
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-700">Intervall</label>
              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              >
                <option value="">Einmalig</option>
                <option value="DAY">Tag</option>
                <option value="WEEK">Woche</option>
                <option value="MONTH">Monat</option>
                <option value="YEAR">Jahr</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Dauer (Anzahl Intervalle)</label>
              <input
                type="number"
                min={0}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
                disabled={!interval}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              id="show_on_social_media"
              type="checkbox"
              checked={showOnSocial}
              onChange={(e) => setShowOnSocial(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <label htmlFor="show_on_social_media" className="text-xs font-medium text-gray-700">
              Auf Social Media hervorheben
            </label>
          </div>

          <div className="mt-3 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div>
              <Label className="text-xs font-medium text-gray-700">Art der Leistung</Label>
              <p className="text-[11px] text-gray-500">
                Steuert, ob diese Leistung als Spende oder als Sponsoring behandelt wird.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500">Sponsoring</span>
              <Switch checked={isDonation} onCheckedChange={setIsDonation} />
              <span className="text-[11px] text-gray-500">Spende</span>
            </div>
          </div>
        </form>
      )}
    />
  );
};

export default EditAdvertisementDialog;

